import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PageTemplate from '../components/Common/PageTemplate';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { trackExternalClick, trackDownload, trackButtonClick } from '../utils/analytics';
import {
    GraduationCap,
    Building,
    FileText,
    MapPin,
    Users,
    Award,
    Target,
    Download,
    ExternalLink,
    CheckCircle,
    Clock,
    Briefcase,
    TrendingUp,
    BookOpen,
    Heart,
    Calculator,
    FlaskConical,
    BarChart3
} from 'lucide-react';

const StajPage = () => {
    const location = useLocation();
    const { i18n } = useTranslation();
    const currentPathLang = location.pathname.startsWith('/en') ? 'en' : 'tr';
    const t = i18n.getFixedT(currentPathLang);
    const [activeTab, setActiveTab] = useState('genel');

    const departmentData = {
        'actuarial': {
            title: t('internship_page.departments_section.actuarial.title'),
            icon: Calculator,
            color: 'from-purple-500 to-indigo-600',
            textColor: 'text-purple-600',
            description: t('internship_page.departments_section.actuarial.description'),
            areas: t('internship_page.departments_section.actuarial.areas', { returnObjects: true }),
            notAccepted: t('internship_page.departments_section.actuarial.not_accepted')
        },
        'biology': {
            title: t('internship_page.departments_section.biology.title'),
            icon: Heart,
            color: 'from-green-500 to-emerald-600',
            textColor: 'text-green-600',
            description: t('internship_page.departments_section.biology.description'),
            areas: t('internship_page.departments_section.biology.areas', { returnObjects: true })
        },
        'statistics': {
            title: t('internship_page.departments_section.statistics.title'),
            icon: BarChart3,
            color: 'from-blue-500 to-cyan-600',
            textColor: 'text-blue-600',
            description: t('internship_page.departments_section.statistics.description'),
            areas: t('internship_page.departments_section.statistics.areas', { returnObjects: true })
        },
        'chemistry': {
            title: t('internship_page.departments_section.chemistry.title'),
            icon: FlaskConical,
            color: 'from-orange-500 to-red-600',
            textColor: 'text-orange-600',
            description: t('internship_page.departments_section.chemistry.description'),
            areas: t('internship_page.departments_section.chemistry.areas', { returnObjects: true })
        },
        'mathematics': {
            title: t('internship_page.departments_section.mathematics.title'),
            icon: BookOpen,
            color: 'from-teal-500 to-green-600',
            textColor: 'text-teal-600',
            description: t('internship_page.departments_section.mathematics.description'),
            areas: t('internship_page.departments_section.mathematics.areas', { returnObjects: true })
        }
    };

    const generalInfo = t('internship_page.general_info.items', { returnObjects: true });

    const benefits = t('internship_page.benefits_section.items', { returnObjects: true });

    const tabData = [
        { key: 'genel', label: t('internship_page.tabs.general'), icon: FileText },
        { key: 'bolumler', label: t('internship_page.tabs.departments'), icon: Building },
        { key: 'faydalar', label: t('internship_page.tabs.benefits'), icon: Award }
    ];

    return (
        <PageTemplate
            title={t('internship_page.title')}
            subtitle={t('internship_page.subtitle')}
            backgroundImage="/fenfakultesivideo.mp4"
            isVideo={true}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                {/* Staj Yönergesi İndirme Kartı */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl shadow-xl p-8 mb-12 text-white"
                >
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-6">
                            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                                <Download className="w-8 h-8" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold mb-2">{t('internship_page.download_directive')}</h2>
                                <p className="text-blue-100">
                                    {t('internship_page.download_directive_desc')}
                                </p>
                            </div>
                        </div>
                        <a
                            href="https://fs.hacettepe.edu.tr/chem/resimler/FEN%20FAK%C3%9CLTES%C4%B0%20STAJ%20Y%C3%96NERGES%C4%B0%20(1).pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl hover:scale-105"
                            onClick={() => trackDownload('Staj Yönergesi', 'PDF')}
                        >
                            <FileText className="w-5 h-5" />
                            {t('internship_page.download_button')}
                            <ExternalLink className="w-4 h-4" />
                        </a>
                    </div>
                </motion.div>

                {/* Tab Navigation */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-8">
                    <div className="flex flex-wrap border-b border-gray-100">
                        {tabData.map((tab) => {
                            const IconComponent = tab.icon;
                            return (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`flex-1 min-w-0 px-6 py-4 text-center transition-all duration-300 ${
                                        activeTab === tab.key
                                            ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'
                                            : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                                    } ${tab.key === 'genel' ? 'rounded-tl-2xl' : ''} ${
                                        tab.key === 'faydalar' ? 'rounded-tr-2xl' : ''
                                    }`}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <IconComponent className="w-5 h-5" />
                                        <span className="font-medium text-sm sm:text-base">{tab.label}</span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="p-8">
                        {/* Genel Bilgiler */}
                        {activeTab === 'genel' && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="space-y-8"
                            >
                                <div className="text-center mb-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('internship_page.general_info.title')}</h3>
                                    <p className="text-gray-600 max-w-3xl mx-auto">
                                        {t('internship_page.general_info.description')}
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {generalInfo && Array.isArray(generalInfo) && generalInfo.map((info, index) => {
                                        const IconComponent = {
                                            Clock: Clock,
                                            FileText: FileText,
                                            Award: Award,
                                            CheckCircle: CheckCircle,
                                            ExternalLink: ExternalLink
                                        }[info.icon];
                                        return (
                                            <motion.div
                                                key={index}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.1 }}
                                                className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300"
                                            >
                                                <div className="flex items-start gap-4">
                                                    <div className={`w-12 h-12 rounded-lg bg-white flex items-center justify-center shadow-md`}>
                                                        <IconComponent className={`w-6 h-6 ${info.color}`} />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900 mb-2">{info.title}</h4>
                                                        <p className="text-gray-600 text-sm">{info.description}</p>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>

                                {/* Detaylı Bilgi Butonu */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5 }}
                                    className="text-center mt-8"
                                >
                                    <a
                                        href="https://sksdb.hacettepe.edu.tr/bidbnew/post.php?id=7&title=stajyer-ogrenci"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                                        onClick={() => trackExternalClick('Staj İşlemleri ve Sigorta Detayları', 'https://sksdb.hacettepe.edu.tr/bidbnew/post.php?id=7&title=stajyer-ogrenci')}
                                    >
                                        <ExternalLink className="w-5 h-5" />
                                        {t('internship_page.general_info.external_link_button')}
                                        <span className="text-xs bg-white/20 px-2 py-1 rounded-full">SKS</span>
                                    </a>
                                    <p className="text-gray-600 text-sm mt-3">
                                        {t('internship_page.general_info.external_link_note')}
                                    </p>
                                </motion.div>
                            </motion.div>
                        )}

                        {/* Bölüm Staj Alanları */}
                        {activeTab === 'bolumler' && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="space-y-8"
                            >
                                <div className="text-center mb-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('internship_page.departments_section.title')}</h3>
                                    <p className="text-gray-600 max-w-3xl mx-auto">
                                        {t('internship_page.departments_section.description')}
                                    </p>
                                </div>

                                <div className="space-y-8">
                                    {Object.entries(departmentData).map(([key, dept], index) => {
                                        const IconComponent = dept.icon;
                                        return (
                                            <motion.div
                                                key={key}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: index * 0.1 }}
                                                className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
                                            >
                                                <div className={`bg-gradient-to-r ${dept.color} p-6 text-white`}>
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                                            <IconComponent className="w-6 h-6" />
                                                        </div>
                                                        <div>
                                                            <h4 className="text-xl font-bold">{dept.title}</h4>
                                                            <p className="text-white/80">{dept.description}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="p-6">
                                                    <h5 className="font-semibold text-gray-900 mb-4">{t('internship_page.departments_section.areas_title')}</h5>
                                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                                        {dept.areas && Array.isArray(dept.areas) && dept.areas.map((area, areaIndex) => (
                                                            <div key={areaIndex} className="flex items-start gap-3">
                                                                <CheckCircle className={`w-5 h-5 mt-0.5 ${dept.textColor} flex-shrink-0`} />
                                                                <span className="text-gray-700 text-sm">{area}</span>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {dept.notAccepted && (
                                                        <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
                                                            <p className="text-red-700 text-sm font-medium">
                                                                <strong>{t('internship_page.departments_section.note')}:</strong> {dept.notAccepted}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        )}

                        {/* Stajın Faydaları */}
                        {activeTab === 'faydalar' && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="space-y-8"
                            >
                                <div className="text-center mb-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('internship_page.benefits_section.title')}</h3>
                                    <p className="text-gray-600 max-w-3xl mx-auto">
                                        {t('internship_page.benefits_section.description')}
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {benefits && Array.isArray(benefits) && benefits.map((benefit, index) => {
                                        const IconComponent = {
                                            Target: Target,
                                            Users: Users,
                                            TrendingUp: TrendingUp,
                                            Briefcase: Briefcase
                                        }[benefit.icon];
                                        return (
                                            <motion.div
                                                key={index}
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ delay: index * 0.1 }}
                                                className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 text-center hover:shadow-lg transition-all duration-300"
                                            >
                                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                                    <IconComponent className="w-8 h-8 text-white" />
                                                </div>
                                                <h4 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h4>
                                                <p className="text-gray-600">{benefit.description}</p>
                                            </motion.div>
                                        );
                                    })}
                                </div>

                                <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-8 text-white text-center">
                                    <GraduationCap className="w-16 h-16 mx-auto mb-4" />
                                    <h4 className="text-2xl font-bold mb-4">{t('internship_page.benefits_section.success_message.title')}</h4>
                                    <p className="text-green-100 max-w-2xl mx-auto">
                                        {t('internship_page.benefits_section.success_message.description')}
                                    </p>
                                </div>
                            </motion.div>
                        )}
                    </div>
                </div>
            </div>
        </PageTemplate>
    );
};

export default StajPage;
